import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Leads → To-Dos (C-16 S6, GLE-8406): the caller's daily to-dos, Command
 * Center's daily to-do gate on the platform. Every route is behind the
 * backend switch `daily_todos_enabled`: while it is off no route is
 * registered and each answers 404 (error_code 1035), which the app reads as
 * "To-Dos is not switched on".
 */

export const DAILY_TODO_SECTIONS = {
  TODAY: "today",
  TOMORROW: "tomorrow",
  NO_SHOW: "no_show",
} as const;

export type DailyTodoSectionKeyT =
  (typeof DAILY_TODO_SECTIONS)[keyof typeof DAILY_TODO_SECTIONS];

export interface DailyTodoCardI {
  /** Stable for the day; a rebooked meeting is a new card. */
  key: string;
  section: DailyTodoSectionKeyT;
  campaign_id: string;
  campaign_name: string;
  account_id: string;
  account_name: string;
  contact_id: string;
  contact_name: string;
  /** The meeting (today/tomorrow) or the missed meeting (no-show). */
  meeting_at: string | null;
  /** On a no-show card: "no_show" or "canceled". */
  outcome?: "no_show" | "canceled";
  /** The campaign's caller bounty, in cents. */
  payout: number;
  worked: boolean;
  /** click: opened from the list; activity: worked in the app today. */
  worked_source?: "click" | "activity";
}

export interface DailyTodoSectionI {
  key: DailyTodoSectionKeyT;
  total: number;
  worked: number;
  cards: Array<DailyTodoCardI>;
}

export interface DailyTodoSettingsI {
  /** "HH:MM" in `time_zone`. */
  daily_time: string;
  time_zone: string;
  intro_done: boolean;
}

export interface DailyTodoStateI {
  total: number;
  worked: number;
  complete: boolean;
  briefing_seen: boolean;
  snoozed_until: string | null;
  dismissed: boolean;
  /** The snooze ran out and cards are left. */
  must_finish: boolean;
  /** A snooze time is still open today and none was used. */
  can_snooze: boolean;
  /** The caller may stop for today. */
  can_dismiss: boolean;
  /** When today's reminder is due (ISO). */
  reminder_at: string;
}

export interface GetDailyTodosResponseI {
  /** The ET day, YYYY-MM-DD. */
  day: string;
  /** The server's clock (ISO). */
  now: string;
  settings: DailyTodoSettingsI;
  state: DailyTodoStateI;
  sections: Array<DailyTodoSectionI>;
  /** Every card the caller has worked, Command Center's included. */
  lifetime_worked: number;
  /** The header's "Today's to-dos" catch-up count. */
  catchup_count: number;
}

export interface SaveDailyTodoSettingsRequestI {
  daily_time: string;
  time_zone: string;
  intro_done?: boolean;
}

/** Error codes of the To-Dos routes (backend errors.go 1310–1313). */
export const DAILY_TODO_ERROR_CODES = {
  BAD_SNOOZE: 1310,
  MUST_FINISH: 1311,
  CARD_NOT_FOUND: 1312,
  BAD_SETTINGS: 1313,
} as const;

/** A 404 here means "switched off": an answer, not an error to log. */
const switchProbeConfig = (): AxiosRequestConfig &
  AxiosInstanceInterceptorRequestExtensionI => ({
  meta: { expectedStatuses: [404] },
});

export const DailyTodosAPIEndpoints = (api: AxiosInstance) => ({
  getDailyTodos: () =>
    api.get<GetDailyTodosResponseI>("/v1/daily_todos", switchProbeConfig()),

  workDailyTodo: (cardKey: string) =>
    api.post("/v1/daily_todos/work", { card_key: cardKey }),

  snoozeDailyTodos: (until: string) =>
    api.post("/v1/daily_todos/snooze", { until }),

  dismissDailyTodos: () => api.post("/v1/daily_todos/dismiss"),

  markDailyTodosBriefingSeen: () => api.post("/v1/daily_todos/briefing_seen"),

  saveDailyTodoSettings: (body: SaveDailyTodoSettingsRequestI) =>
    api.put("/v1/daily_todos/settings", body),
});
